<script setup lang="ts">
import { Eye, EyeOff, Loader2 } from 'lucide-vue-next'

import type { AuthFormData, AuthInputErrors } from '@/types/types'

const showPassword = ref(false)
const showConfirmPassword = ref(false)

const { isEmailValid } = useRegex()
const { t } = useI18n()

const emit = defineEmits<{
  (e: 'onSubmit', data: AuthFormData): void
}>()

const props = defineProps<{
  isLoaded: boolean
  pageType?: 'Login' | 'ResetPassword' | 'ForgotPassword'
}>()

const route = useRoute()

const user = ref<AuthFormData>({
  email: '',
  password: '',
  confirmPassword: '',
  rememberMe: false
})

const inputErrors = ref<AuthInputErrors>({
  email: [],
  confirmPassword: [],
  password: []
})

const passwordPlaceholder = computed(() =>
  props.pageType === 'Login' ? '' : t('signup.account.form.placeholder.password')
)

const checkInputValidity = (input: string) => {
  inputErrors.value[input] = []
  if (input === 'email' && user.value.email.length > 0 && !isEmailValid(user.value.email)) {
    inputErrors.value.email = [t('signup.account.form.errors.email')]
  }

  if (props.pageType === 'ForgotPassword') {
    return
  }

  if (input === 'password' && !props.pageType && user.value.password.length < 8) {
    inputErrors.value.password = [t('signup.account.form.errors.password')]
  }

  if (input === 'password' && !user.value.password.replace(/\s/g, '').length) {
    inputErrors.value.password = [t('signup.account.form.errors.passwordSpaces')]
  }

  if (
    props.pageType === 'ResetPassword' &&
    input === 'confirmPassword' &&
    user.value.confirmPassword !== user.value.password
  ) {
    inputErrors.value.confirmPassword = [t('login.reset.error.confirm_password_different')]
  }
}

const hasErrors = computed(() => {
  return Object.values(inputErrors.value).some((error) => error.length > 0)
})

const submitButtonText = computed(() => {
  switch (props.pageType) {
    case 'Login':
      return t('login.account.form.login')
    case 'ForgotPassword':
      return t('login.forgot.button.save')
    case 'ResetPassword':
      return t('login.reset.button.save')
    default:
      return t('signup.account.form.create')
  }
})

if (route.query.email) {
  user.value.email = route.query.email.toString()
}

const checkPasswordInput = () => {
  if (inputErrors.value.password.length > 0) {
    checkInputValidity('password')
  }
  if (inputErrors.value.confirmPassword.length > 0) {
    checkInputValidity('confirmPassword')
  }
}

const handleCheckPassword = () => {
  checkInputValidity('password')
  checkInputValidity('confirmPassword')
}

const handleSubmit = async () => {
  if (hasErrors.value) {
    return
  }

  emit('onSubmit', {
    email: user.value.email,
    password: user.value.password,
    rememberMe: user.value.rememberMe
  })
}
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <label for="email" class="block text-sm font-medium text-foreground lg:mb-2">
      {{ t('signup.account.form.labels.email') }}
    </label>
    <AppFormInput
      id="email"
      v-model="user.email"
      type="email"
      name="email"
      required
      input-classes="h-12 px-4"
      :errors="inputErrors.email"
      @change="checkInputValidity('email')"
      @update:model-value="inputErrors.email.length > 0 && checkInputValidity('email')"
    />
    <template v-if="pageType !== 'ForgotPassword'">
      <label for="password" class="mt-4 block text-sm font-medium text-foreground lg:mb-2">
        {{ t('signup.account.form.labels.password') }}
      </label>
      <div>
        <div class="relative flex w-full">
          <Input
            id="password"
            v-model="user.password"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            required
            :placeholder="passwordPlaceholder"
            class="h-12 px-4 pr-12 sm:text-sm"
            :aria-invalid="inputErrors.password?.length > 0 || undefined"
            @change="handleCheckPassword"
            @update:model-value="checkPasswordInput"
          />
          <button
            type="button"
            class="absolute inset-y-0 right-0 flex cursor-pointer items-center px-3 text-foreground-secondary"
            :aria-label="showPassword ? t('login.hidePassword') : t('login.showPassword')"
            @click="showPassword = !showPassword"
          >
            <EyeOff v-if="showPassword" class="h-5 w-5" />
            <Eye v-else class="h-5 w-5" />
          </button>
        </div>
        <p v-if="inputErrors.password?.length > 0" class="mt-2 text-sm text-red-600">
          {{ inputErrors.password[0] }}
        </p>
      </div>
      <template v-if="pageType === 'ResetPassword'">
        <label for="password" class="mt-4 block text-sm font-medium text-foreground lg:mb-2">
          {{ t('login.reset.confirmNewPassword') }}
        </label>
        <div>
          <div class="relative flex w-full">
            <Input
              id="confirmPassword"
              v-model="user.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              name="confirmPassword"
              required
              class="h-12 px-4 pr-12 sm:text-sm"
              :aria-invalid="inputErrors.confirmPassword?.length > 0 || undefined"
              @change="handleCheckPassword"
              @update:model-value="handleCheckPassword"
            />
            <button
              type="button"
              class="absolute inset-y-0 right-0 flex cursor-pointer items-center px-3 text-foreground-secondary"
              :aria-label="showConfirmPassword ? t('login.hidePassword') : t('login.showPassword')"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <EyeOff v-if="showConfirmPassword" class="h-5 w-5" />
              <Eye v-else class="h-5 w-5" />
            </button>
          </div>
          <p v-if="inputErrors.confirmPassword?.length > 0" class="mt-2 text-sm text-red-600">
            {{ inputErrors.confirmPassword[0] }}
          </p>
        </div>
      </template>

      <div v-if="pageType === 'Login'" class="mt-5 flex items-center justify-between">
        <AppCheckboxField id="remember_me" v-model="user.rememberMe" :label="t('login.rememberMe')" />

        <div class="text-sm">
          <router-link class="font-medium text-cta hover:text-cta/70" :to="{ name: 'ForgotPassword' }">
            {{ t('login.forgotPassword') }}
          </router-link>
        </div>
      </div>
    </template>
    <Button :disabled="!isLoaded" type="submit" variant="default-primary" size="xl" class="mt-5 w-full">
      <Loader2 v-if="!isLoaded" class="animate-spin" />
      {{ submitButtonText }}
    </Button>
  </form>
</template>
